/**
 * Les colonnes d'un tableau d'éléments concernés.
 *
 * Radar et le Diagnostic listent tous deux les `affectedItems` d'un constat. Ils les rendaient
 * en rangées libres — un nom à gauche, une valeur poussée à droite —, et rien ne disait ce
 * qu'on lisait : « [FR] [BRAND] [P] > Marque Générique » n'annonce ni qu'il s'agit d'une
 * campagne, ni que ce qui suit le chevron est un groupe d'annonces. Trente lignes ainsi
 * empilées se parcourent à l'œil, pas à la lecture.
 *
 * ## Pourquoi les en-têtes se dérivent, au lieu d'être déclarées par contrôle
 *
 * Le contrat d'un élément est `{ id, name, type, value, url, cible }` (`BaseCheck.createAffectedItem`),
 * et `value` est **libre** : un score, une URL, « CPA actuel … vs référence … », ou le chemin
 * « campagne > groupe ». Pire, ce chemin se range tantôt dans `name` (`AdGroupNoAdCheck`,
 * `DisapprovedAdCheck`), tantôt dans `value` (`KeywordNoImpressionCheck`,
 * `KeywordNegativeConflictCheck`) : il n'y a pas de place fixe à nommer.
 *
 * Une table « contrôle → colonnes » tenue à la main réclamerait donc une entrée pour chacun des
 * cinquante-cinq contrôles, et une de plus à chaque ajout — avec, pour un oubli, un tableau
 * dont les en-têtes mentent. Les colonnes se déduisent ici de ce que la liste **contient**, ce
 * qui se vérifie à chaque rendu.
 *
 * ## Les trois règles
 *
 * 1. `type` dit ce que `name` désigne : c'est l'en-tête de la première colonne.
 * 2. Un champ de la forme `A > B` se scinde en « Campagne » et « Groupe d'annonces » — mais
 *    seulement si **tous** les éléments de la liste s'y prêtent, et avec exactement deux
 *    segments. ⚠️ Un nom de campagne contenant lui-même un chevron en produirait trois : la
 *    liste reste alors d'un bloc, ce qui est juste, là où scinder aurait réparti au hasard.
 * 3. À défaut, `value` est une colonne unique — « URL de destination » si toutes les valeurs en
 *    sont une, « Détail » sinon.
 */

/** Ce que `name` désigne, selon le `type` posé par le contrôle. */
const EN_TETE_PAR_TYPE = {
  keyword: 'Mot-clé',
  campaign: 'Campagne',
  ad_group: "Groupe d'annonces",
  ad: 'Annonce',
  url: 'URL',
  conversion_action: 'Action de conversion',
  device: 'Appareil',
  asset: 'Composant',
  search_term: 'Terme de recherche',
  product: 'Produit'
};

const SEPARATEUR = ' > ';

const estUneUrl = (valeur) => /^https?:\/\//i.test(String(valeur));

const estRenseigne = (valeur) => valeur !== null && valeur !== undefined && valeur !== '';

/**
 * Le couple campagne / groupe d'annonces d'un champ, ou `null` s'il n'en porte pas.
 *
 * Exactement deux segments, tous deux non vides : « Marque > Générique » se scinde,
 * « Marque > Générique > Exact » non — voir la règle 2 de l'en-tête.
 */
const chemin = (valeur) => {
  if (typeof valeur !== 'string') return null;
  const segments = valeur.split(SEPARATEUR);
  if (segments.length !== 2) return null;
  return segments.every(s => s.trim()) ? segments.map(s => s.trim()) : null;
};

/** L'en-tête de la colonne des noms, quand tous les éléments partagent un même `type`. */
const enTeteDuType = (elements) => {
  const types = new Set(elements.map(e => e.type).filter(Boolean));
  if (types.size !== 1) return 'Élément';
  return EN_TETE_PAR_TYPE[[...types][0]] || 'Élément';
};

const COLONNES_DU_CHEMIN = (lire) => ([
  { entete: 'Campagne', lire: e => (chemin(lire(e)) || [])[0] },
  { entete: "Groupe d'annonces", lire: e => (chemin(lire(e)) || [])[1] }
]);

/**
 * Les colonnes à rendre pour une liste d'éléments concernés.
 *
 * Chaque colonne porte son `entete`, une fonction `lire(element)` qui en extrait le texte, et
 * deux drapeaux de mise en forme : `lien` (la valeur est une adresse à ouvrir) et `multiligne`
 * (la valeur peut énumérer, et son `\n` doit être conservé — c'est le cas de la décomposition
 * d'un Quality Score).
 *
 * @param {Array} elements - les `affectedItems` du constat
 * @returns {Array<{entete: string, lire: Function, lien?: boolean, multiligne?: boolean, principale?: boolean}>}
 */
export function colonnesDesElements(elements = []) {
  const utiles = (elements || []).filter(Boolean);
  if (utiles.length === 0) return [];

  const colonnes = [];

  // --- Le nom. Un chemin y prime sur le type : « Campagne | Groupe » dit plus que « Annonce ».
  if (utiles.every(e => chemin(e.name))) {
    colonnes.push(...COLONNES_DU_CHEMIN(e => e.name));
  } else {
    colonnes.push({ entete: enTeteDuType(utiles), lire: e => e.name, principale: true });
  }

  // --- La valeur, si au moins un élément en porte une.
  const avecValeur = utiles.filter(e => estRenseigne(e.value));
  if (avecValeur.length === 0) return colonnes;

  const dejaScinde = colonnes.some(c => c.entete === 'Campagne');

  if (!dejaScinde && avecValeur.every(e => chemin(e.value))) {
    colonnes.push(...COLONNES_DU_CHEMIN(e => e.value));
  } else if (avecValeur.every(e => estUneUrl(e.value))) {
    colonnes.push({ entete: 'URL de destination', lire: e => e.value, lien: true });
  } else {
    colonnes.push({ entete: 'Détail', lire: e => e.value, multiligne: true });
  }

  return colonnes;
}

export default colonnesDesElements;
